import { useMutation, useQuery } from '@apollo/client'
import { ApolloCacheKey } from '@deltek/specification-client/core/Configurations/apolloBaseClient'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import { InsertCurrentUserInfoMutation } from '@deltek/specification-client/core/GraphQL/Mutations/currentUserInfo.mutations'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import React, { useContext, useEffect, useRef } from 'react'
import AuthEventService from 'shared/services/authEventService'

/**
 * Manages user session lifecycle and emits auth events.
 * Detects login/logout for cache invalidation.
 */
const CurrentUserConfiguration: React.FC = ({ children }) => {
    const { currentUserInfo } = useContext(AuthContext)
    const previousUserInfo = useRef(currentUserInfo)
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const [insertCurrentUserInfo] = useMutation<CurrentUserInfoResponse>(
        InsertCurrentUserInfoMutation
    )

    useEffect(() => {
        const savedUser = currentUserInfoResponse?.currentUserInfo
        const differentPerson =
            !!savedUser?.b2cUserId &&
            savedUser.b2cUserId !== currentUserInfo?.b2cUserId
        const accountChanged =
            !!savedUser &&
            savedUser.account.accountId !== currentUserInfo?.account?.accountId

        if (differentPerson) {
            // A different person logged in, so clear the saved data and reload so the previous user's info doesn't carry over.
            localStorage.removeItem(ApolloCacheKey)
            window.location.reload()
        } else if (currentUserInfo && (!savedUser || accountChanged)) {
            // Save the user's info if none is saved yet, and re-save it when they log in under a different account so the app stops using the old account's info.
            insertCurrentUserInfo({
                variables: {
                    currentUserInfo,
                },
            })
        }

        // Emit auth events for login/logout
        if (previousUserInfo.current && !currentUserInfo) {
            AuthEventService.emitLogout()
        } else if (!previousUserInfo.current && currentUserInfo) {
            AuthEventService.emitLogin()
        }

        previousUserInfo.current = currentUserInfo
    }, [currentUserInfo, currentUserInfoResponse, insertCurrentUserInfo])

    return <>{children}</>
}

export default CurrentUserConfiguration
